import {useCallback, useEffect, useRef, useState} from "react";

// Fenêtre flottante (déplaçable, redimensionnable) pour les éditeurs de la landing.
// La géométrie est en fonctions pures (testées) ; le hook ne fait que brancher les événements du pointeur.

export const MIN_WIDTH = 360;
export const MIN_HEIGHT = 260;
const MARGIN = 8; // marge minimale gardée visible autour de la fenêtre
const HEADER_VISIBLE = 40; // au moins la barre de titre reste atteignable, même poussée hors de l'écran
const STORAGE_KEY = "paroles_agents_editor_rect";

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

// Fenêtre centrée de taille par défaut, bornée à l'écran.
export function defaultRect(vw, vh) {
    const w = Math.min(760, vw - 2 * MARGIN);
    const h = Math.min(Math.round(vh * 0.85), vh - 2 * MARGIN);
    return {x: Math.round((vw - w) / 2), y: Math.round((vh - h) / 2), w, h};
}

// Garde la fenêtre utilisable : taille mini/maxi, barre de titre toujours atteignable.
export function clampRect(rect, vw, vh) {
    const w = clamp(rect.w, Math.min(MIN_WIDTH, vw - 2 * MARGIN), vw - 2 * MARGIN);
    const h = clamp(rect.h, Math.min(MIN_HEIGHT, vh - 2 * MARGIN), vh - 2 * MARGIN);
    const x = clamp(rect.x, HEADER_VISIBLE - w, vw - HEADER_VISIBLE);
    const y = clamp(rect.y, 0, vh - HEADER_VISIBLE);
    return {x, y, w, h};
}

export function moveRect(start, dx, dy, vw, vh) {
    return clampRect({...start, x: start.x + dx, y: start.y + dy}, vw, vh);
}

// dir : n, s, e, w, ne, nw, se, sw. Le bord opposé reste immobile ; la taille mini/maxi et les limites de l'écran
// rognent le bord que l'on tire (jamais le bord opposé, qui ne doit pas bouger).
export function resizeRect(start, dir, dx, dy, vw, vh) {
    let left = start.x, top = start.y, right = start.x + start.w, bottom = start.y + start.h;
    if (dir.includes("e")) right = Math.min(right + dx, Math.max(vw, right));
    if (dir.includes("s")) bottom = Math.min(bottom + dy, Math.max(vh, bottom));
    if (dir.includes("w")) left = Math.max(left + dx, Math.min(0, left));
    if (dir.includes("n")) top = Math.max(top + dy, Math.min(0, top));
    const minW = Math.min(MIN_WIDTH, vw - 2 * MARGIN);
    const minH = Math.min(MIN_HEIGHT, vh - 2 * MARGIN);
    const maxW = vw - 2 * MARGIN;
    const maxH = vh - 2 * MARGIN;
    if (right - left < minW) { if (dir.includes("w")) left = right - minW; else right = left + minW; }
    if (right - left > maxW) { if (dir.includes("w")) left = right - maxW; else right = left + maxW; }
    if (bottom - top < minH) { if (dir.includes("n")) top = bottom - minH; else bottom = top + minH; }
    if (bottom - top > maxH) { if (dir.includes("n")) top = bottom - maxH; else bottom = top + maxH; }
    return clampRect({x: left, y: top, w: right - left, h: bottom - top}, vw, vh);
}

// Clavier (alternative au glisser, WCAG 2.5.7) : flèches = déplacer, Maj + flèches = redimensionner (coin bas-droit).
// Renvoie null si la touche n'est pas une flèche.
export const KEY_STEP = 16;
const ARROWS = {ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1]};
export function keyboardRect(start, key, shift, vw, vh) {
    const arrow = ARROWS[key];
    if (!arrow) return null;
    const dx = arrow[0] * KEY_STEP;
    const dy = arrow[1] * KEY_STEP;
    return shift ? resizeRect(start, "se", dx, dy, vw, vh) : moveRect(start, dx, dy, vw, vh);
}

const sameRect = (a, b) => a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;

function readStoredRect() {
    try {
        const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
        if (parsed && ["x", "y", "w", "h"].every(k => Number.isFinite(parsed[k]))) return parsed;
    } catch (e) { /* stockage indisponible ou illisible : position par défaut */ }
    return null;
}

function storeRect(rect) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(rect)); } catch (e) { /* confort seulement */ }
}

export default function useFloatingPanel() {
    const viewport = () => ({vw: window.innerWidth, vh: window.innerHeight});
    const [rect, setRect] = useState(() => {
        const {vw, vh} = viewport();
        return clampRect(readStoredRect() || defaultRect(vw, vh), vw, vh);
    });
    const rectRef = useRef(rect);
    rectRef.current = rect;
    const gesture = useRef(null); // {mode: "move" | dir, startX, startY, start}

    // Redimensionnement de la fenêtre du navigateur : la fenêtre flottante reste dans l'écran.
    useEffect(() => {
        const onResize = () => { const {vw, vh} = viewport(); setRect(r => clampRect(r, vw, vh)); };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const begin = useCallback((mode) => (e) => {
        if (e.button !== undefined && e.button !== 0) return;
        // Un clic sur un bouton de la barre de titre (fermer…) ne déclenche pas de déplacement.
        if (mode === "move" && e.target.closest && e.target.closest("button")) return;
        e.preventDefault();
        gesture.current = {mode, startX: e.clientX, startY: e.clientY, start: rectRef.current};
        e.currentTarget.setPointerCapture?.(e.pointerId);
    }, []);

    const update = useCallback((e) => {
        const g = gesture.current;
        if (!g) return;
        const {vw, vh} = viewport();
        const dx = e.clientX - g.startX;
        const dy = e.clientY - g.startY;
        const next = g.mode === "move" ? moveRect(g.start, dx, dy, vw, vh) : resizeRect(g.start, g.mode, dx, dy, vw, vh);
        rectRef.current = next; // à jour tout de suite : un pointerup arrivé avant le rendu enregistre la bonne géométrie
        setRect(next);
    }, []);

    const end = useCallback((e) => {
        const g = gesture.current;
        if (!g) return;
        gesture.current = null;
        e.currentTarget.releasePointerCapture?.(e.pointerId);
        if (!sameRect(rectRef.current, g.start)) storeRect(rectRef.current); // un simple clic ne réécrit rien
    }, []);

    // Props de la barre de titre pour le clavier (flèches : déplacer, Maj + flèches : redimensionner).
    const onKeyDown = useCallback((e) => {
        if (e.target !== e.currentTarget) return; // les touches d'un bouton de la barre (Fermer) ne déplacent pas la fenêtre
        const {vw, vh} = viewport();
        const next = keyboardRect(rectRef.current, e.key, e.shiftKey, vw, vh);
        if (!next) return;
        e.preventDefault();
        rectRef.current = next;
        setRect(next);
        storeRect(next);
    }, []);

    // Props à poser sur la barre de titre (déplacer) ou sur une poignée (redimensionner).
    const handleProps = useCallback((mode) => ({onPointerDown: begin(mode), onPointerMove: update, onPointerUp: end, onPointerCancel: end}), [begin, update, end]);

    return {rect, handleProps, onKeyDown};
}
